import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { DEFAULT_DOCUMENT_OPTIONS } from "utils/constants/planShipment";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import {
  Camera,
  CheckCircle2,
  ChevronLeft,
  FileText,
  Info,
  Maximize2,
  PlusCircle,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL } from "utils/constants/others";
import { isImageUrl, isPdfUrl } from "utils/pdfUtils";
import {
  formatInvoiceAmount,
  formatShippingNoteNumber,
  formatWeight,
  formatWeightValue,
  hasShippingNote,
} from "./planShipmentUtils";
import {
  convertInvoiceImagesToPdf,
  createInvoiceDraft,
  getInvoiceErrorMessage,
  INVOICE_UPLOAD_TYPES,
  isSupportedInvoiceImage,
  isSupportedInvoicePdf,
  mapDeletedInvoiceToRequest,
  mapNewInvoiceToRequest,
  MAX_INVOICE_FILE_SIZE_BYTES,
} from "./planShipmentInvoiceUtils";
import usePlanShipmentStore from "./usePlanShipmentStore";
import { HeaderIconButton } from "./styles.planShipment";
import {
  AddImageTile,
  AddInvoiceButton,
  CardError,
  DeleteIconButton,
  EditorInvoiceList,
  ExistingInvoiceCard,
  ExistingInvoiceCopy,
  ExistingInvoiceDocumentFallback,
  ExistingInvoiceThumbnail,
  FieldInput,
  FieldLabel,
  FieldSuffix,
  FieldWrapper,
  HiddenFileInput,
  ImagePreview,
  ImagePreviewGrid,
  InvoiceEditorContent,
  InvoiceEditorFooter,
  InvoiceEditorFrame,
  InvoiceEditorHeader,
  InvoiceEditorHeaderCopy,
  InvoiceEditorScrollArea,
  InvoiceEditorShell,
  InvoiceFields,
  InvoiceInfo,
  ModalActions,
  ModalCard,
  ModalIcon,
  ModalOverlay,
  ModalPrimaryButton,
  ModalSecondaryButton,
  ModalSheet,
  NewInvoiceCard,
  NewInvoiceHeader,
  PageError,
  PdfPreview,
  RemoveFileButton,
  SheetButtonRow,
  SubmitInvoicesButton,
  UploadArea,
  UploadOptionButton,
  UploadOptions,
} from "./styles.scheduleInvoiceEditor";
import {
  EmptyState,
  ErrorMessage,
  RetryButton,
  SkeletonCard,
  SkeletonLine,
  StateTitle,
} from "./styles.planShipment";
import ResponsiveModal from "components/core/Common/Modals/ResponsiveModal";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const EditorPdfThumbnail = ({ url }) => {
  const [loadError, setLoadError] = useState(false);

  if (loadError) {
    return (
      <ExistingInvoiceDocumentFallback>
        <FileText aria-hidden="true" />
      </ExistingInvoiceDocumentFallback>
    );
  }

  return (
    <Document
      file={url}
      loading={null}
      onLoadError={() => setLoadError(true)}
      onSourceError={() => setLoadError(true)}
      options={DEFAULT_DOCUMENT_OPTIONS}
    >
      <Page
        pageNumber={1}
        width={60}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        loading={null}
        onRenderError={() => setLoadError(true)}
      />
    </Document>
  );
};

const PAGE_SIZE = 20;
const INVOICE_NUMBER_REGEX = /^[0-9a-zA-Z/\\.\-&#_()|\]]{1,50}$/;

const ScheduleInvoiceEditorSkeleton = () => (
  <InvoiceEditorContent aria-label="Loading PO schedule invoices">
    <SkeletonCard>
      <SkeletonLine $height="48px" />
    </SkeletonCard>
    {[0, 1].map((item) => (
      <SkeletonCard key={item}>
        <SkeletonLine $width="34%" $height="22px" />
        <SkeletonLine $height="110px" />
        <SkeletonLine $height="92px" />
      </SkeletonCard>
    ))}
  </InvoiceEditorContent>
);

const ScheduleInvoiceEditor = () => {
  const navigate = useNavigate();
  const { scheduleGroupId, scheduleId } = useParams();
  const draftSequence = useRef(1);
  const draftsRef = useRef([]);
  const initializedScheduleId = useRef(null);
  const [drafts, setDrafts] = useState([]);
  const [deletedInvoices, setDeletedInvoices] = useState([]);
  const [draftErrors, setDraftErrors] = useState({});
  const [pageError, setPageError] = useState("");
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [showQuantityWarning, setShowQuantityWarning] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isPreparingSubmission, setIsPreparingSubmission] = useState(false);
  const [invoiceImageErrors, setInvoiceImageErrors] = useState({});
  const {
    getPOScheduleGroupDetails,
    getPOScheduleGroupDetailsStatus,
    poScheduleGroupDetails,
    poScheduleGroupDetailsError,
    uploadPlanShipmentInvoiceDocument,
    updatePOScheduleInvoices,
    updatePOScheduleInvoicesStatus,
    resetUpdatePOScheduleInvoices,
    planShipmentAsnConfig,
    getPlanShipmentHelpSections,
  } = usePlanShipmentStore();

  const invoiceAmountBufferPercent = (() => {
    const raw = Number(planShipmentAsnConfig?.invoice_amount_buffer_over_po_amount_percent);
    return Number.isFinite(raw) && raw >= 0 ? raw : 0;
  })();

  const vendorId = window.localStorage.getItem("outlet_id");
  const parsedScheduleGroupId = Number(scheduleGroupId);
  const parsedScheduleId = Number(scheduleId);
  const hasValidParams =
    Number.isInteger(Number(vendorId)) &&
    Number(vendorId) > 0 &&
    Number.isInteger(parsedScheduleGroupId) &&
    parsedScheduleGroupId > 0 &&
    Number.isInteger(parsedScheduleId) &&
    parsedScheduleId > 0;

  const fetchScheduleGroupDetails = useCallback(() => {
    if (!hasValidParams) return;

    getPOScheduleGroupDetails({
      vendor_id: vendorId,
      schedule_group_id: parsedScheduleGroupId,
      page_size: PAGE_SIZE,
      page_number: 1,
    });
  }, [
    getPOScheduleGroupDetails,
    hasValidParams,
    parsedScheduleGroupId,
    vendorId,
  ]);

  useEffect(() => {
    resetUpdatePOScheduleInvoices();
    fetchScheduleGroupDetails();
  }, [fetchScheduleGroupDetails, resetUpdatePOScheduleInvoices]);

  useEffect(() => {
    if (!planShipmentAsnConfig && hasValidParams) {
      const vendorIdLocal = window.localStorage.getItem("outlet_id");
      getPlanShipmentHelpSections({ seller_outlet_id: vendorIdLocal });
    }
  }, []);

  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  useEffect(
    () => () => {
      draftsRef.current.forEach((draft) => {
        draft.files.forEach((fileItem) => {
          if (fileItem.preview_url) {
            window.URL.revokeObjectURL(fileItem.preview_url);
          }
        });
      });
    },
    []
  );

  const purchaseOrder = useMemo(() => {
    const purchaseOrders = Array.isArray(
      poScheduleGroupDetails?.purchase_orders
    )
      ? poScheduleGroupDetails.purchase_orders
      : [];

    return purchaseOrders.find(
      (item) => Number(item.schedule_id) === parsedScheduleId
    );
  }, [parsedScheduleId, poScheduleGroupDetails]);

  const originalInvoices = useMemo(
    () =>
      Array.isArray(purchaseOrder?.invoices) ? purchaseOrder.invoices : [],
    [purchaseOrder]
  );
  const deletedInvoiceIds = useMemo(
    () =>
      new Set(
        deletedInvoices.map((invoice) => Number(invoice.vendor_invoice_id))
      ),
    [deletedInvoices]
  );
  const visibleExistingInvoices = originalInvoices.filter(
    (invoice) => !deletedInvoiceIds.has(Number(invoice.vendor_invoice_id))
  );

  const addDraft = useCallback(() => {
    const draftId = `invoice-${Date.now()}-${draftSequence.current++}`;
    setDrafts((currentDrafts) => [
      ...currentDrafts,
      createInvoiceDraft(draftId),
    ]);
  }, []);

  useEffect(() => {
    if (
      !purchaseOrder ||
      initializedScheduleId.current === purchaseOrder.schedule_id
    ) {
      return;
    }

    initializedScheduleId.current = purchaseOrder.schedule_id;
    if (originalInvoices.length === 0) addDraft();
  }, [addDraft, originalInvoices.length, purchaseOrder]);

  const clearDraftError = (draftId) => {
    setDraftErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[draftId];
      return nextErrors;
    });
  };

  const updateDraft = (draftId, field, value) => {
    setDrafts((currentDrafts) =>
      currentDrafts.map((draft) =>
        draft.client_id === draftId ? { ...draft, [field]: value } : draft
      )
    );
    clearDraftError(draftId);
    setPageError("");
  };

  const removeDraft = (draftId) => {
    setDrafts((currentDrafts) => {
      const removedDraft = currentDrafts.find(
        (draft) => draft.client_id === draftId
      );
      removedDraft?.files.forEach((fileItem) => {
        if (fileItem.preview_url) {
          window.URL.revokeObjectURL(fileItem.preview_url);
        }
      });
      return currentDrafts.filter((draft) => draft.client_id !== draftId);
    });
    clearDraftError(draftId);
  };

  const setFileError = (draftId, message) => {
    setDraftErrors((currentErrors) => ({
      ...currentErrors,
      [draftId]: { message, fields: [] },
    }));
  };

  const handleImageSelection = (draftId, event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (selectedFiles.length === 0) return;

    const draft = drafts.find((item) => item.client_id === draftId);
    if (!draft || draft.upload_type === INVOICE_UPLOAD_TYPES.pdf) {
      setFileError(draftId, "Remove the PDF before adding invoice images.");
      return;
    }
    if (selectedFiles.some((file) => !isSupportedInvoiceImage(file))) {
      setFileError(draftId, "Only JPEG and PNG invoice images are supported.");
      return;
    }
    if (selectedFiles.some((file) => file.size > MAX_INVOICE_FILE_SIZE_BYTES)) {
      setFileError(draftId, "Each image must be 5 MB or smaller.");
      return;
    }

    const currentSize = draft.files.reduce(
      (total, fileItem) => total + fileItem.file.size,
      0
    );
    const selectedSize = selectedFiles.reduce(
      (total, file) => total + file.size,
      0
    );
    if (currentSize + selectedSize > MAX_INVOICE_FILE_SIZE_BYTES) {
      setFileError(
        draftId,
        "The combined invoice images must be 5 MB or smaller."
      );
      return;
    }

    const fileItems = selectedFiles.map((file) => ({
      file,
      preview_url: window.URL.createObjectURL(file),
    }));
    setDrafts((currentDrafts) =>
      currentDrafts.map((item) =>
        item.client_id === draftId
          ? {
              ...item,
              upload_type: INVOICE_UPLOAD_TYPES.images,
              files: [...item.files, ...fileItems],
            }
          : item
      )
    );
    clearDraftError(draftId);
  };

  const handlePdfSelection = (draftId, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const draft = drafts.find((item) => item.client_id === draftId);
    if (!draft || draft.upload_type === INVOICE_UPLOAD_TYPES.images) {
      setFileError(draftId, "Remove the images before uploading a PDF.");
      return;
    }
    if (!isSupportedInvoicePdf(file)) {
      setFileError(draftId, "Please select a valid PDF invoice.");
      return;
    }
    if (file.size > MAX_INVOICE_FILE_SIZE_BYTES) {
      setFileError(draftId, "The invoice PDF must be 5 MB or smaller.");
      return;
    }

    setDrafts((currentDrafts) =>
      currentDrafts.map((item) =>
        item.client_id === draftId
          ? {
              ...item,
              upload_type: INVOICE_UPLOAD_TYPES.pdf,
              files: [{ file, preview_url: "" }],
            }
          : item
      )
    );
    clearDraftError(draftId);
  };

  const removeDraftFile = (draftId, fileIndex) => {
    setDrafts((currentDrafts) =>
      currentDrafts.map((draft) => {
        if (draft.client_id !== draftId) return draft;

        const removedFile = draft.files[fileIndex];
        if (removedFile?.preview_url) {
          window.URL.revokeObjectURL(removedFile.preview_url);
        }
        const files = draft.files.filter((_, index) => index !== fileIndex);
        return {
          ...draft,
          files,
          upload_type: files.length > 0 ? draft.upload_type : "",
        };
      })
    );
    clearDraftError(draftId);
  };

  const validateDrafts = () => {
    const nextErrors = {};
    const invoiceNumbers = new Set(
      visibleExistingInvoices.map((invoice) =>
        String(invoice.invoice_number).trim().toLowerCase()
      )
    );

    drafts.forEach((draft) => {
      const fields = [];
      if (draft.files.length === 0) fields.push("files");
      if (!draft.invoice_number.trim()) fields.push("invoice_number");
      if (
        draft.invoice_number.trim() &&
        !INVOICE_NUMBER_REGEX.test(draft.invoice_number.trim())
      ) {
        fields.push("invoice_number");
      }
      if (!(Number(draft.invoice_amount) > 0)) fields.push("invoice_amount");
      if (!(Number(draft.weight_in_kg) > 0)) fields.push("weight_in_kg");
      if (
        !(Number(draft.number_of_sku) > 0) ||
        !Number.isInteger(Number(draft.number_of_sku))
      ) {
        fields.push("number_of_sku");
      }

      const normalizedInvoiceNumber = draft.invoice_number.trim().toLowerCase();
      if (
        normalizedInvoiceNumber &&
        invoiceNumbers.has(normalizedInvoiceNumber)
      ) {
        if (!fields.includes("invoice_number")) fields.push("invoice_number");
        nextErrors[draft.client_id] = {
          message: "Invoice number must be unique.",
          fields,
        };
      } else if (fields.includes("invoice_number")) {
        nextErrors[draft.client_id] = {
          message: fields.length === 1
            ? "Invoice number is invalid or missing."
            : "Upload the invoice and complete all invoice details.",
          fields,
        };
      } else if (fields.length > 0) {
        nextErrors[draft.client_id] = {
          message: "Upload the invoice and complete all invoice details.",
          fields,
        };
      }
      if (normalizedInvoiceNumber) invoiceNumbers.add(normalizedInvoiceNumber);
    });

    setDraftErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const hasChanges = drafts.length > 0 || deletedInvoices.length > 0;
  const areDraftsComplete = drafts.every(
    (draft) =>
      draft.files.length > 0 &&
      draft.invoice_number.trim() &&
      Number(draft.invoice_amount) > 0 &&
      Number(draft.weight_in_kg) > 0 &&
      Number(draft.number_of_sku) > 0 &&
      Number.isInteger(Number(draft.number_of_sku))
  );
  const isSubmitting =
    isPreparingSubmission || updatePOScheduleInvoicesStatus === STATUS_BEGIN;

  const shouldWarnAboutInvoiceQuantity = () => {
    const existingWeight = visibleExistingInvoices.reduce(
      (total, invoice) => total + (Number(invoice.weight_in_kg) || 0),
      0
    );
    const newWeight = drafts.reduce(
      (total, draft) => total + (Number(draft.weight_in_kg) || 0),
      0
    );

    return (
      existingWeight + newWeight <
      (Number(purchaseOrder?.scheduled_weight_in_kg) || 0)
    );
  };

  const performSubmit = async () => {
    setShowQuantityWarning(false);
    setIsPreparingSubmission(true);
    setPageError("");

    try {
      const newInvoiceRequests = await Promise.all(
        drafts.map(async (draft) => {
          let invoiceFile;
          if (draft.upload_type === INVOICE_UPLOAD_TYPES.images) {
            invoiceFile = await convertInvoiceImagesToPdf(
              draft.files.map((fileItem) => fileItem.file),
              draft.invoice_number
            );
          } else {
            invoiceFile = draft.files[0]?.file;
          }

          if (!invoiceFile) throw new Error("Invoice document is missing");
          if (invoiceFile.size > MAX_INVOICE_FILE_SIZE_BYTES) {
            throw new Error(
              "The generated invoice PDF exceeds 5 MB. Remove an image or upload a smaller file."
            );
          }

          const invoicePath = await uploadPlanShipmentInvoiceDocument(
            invoiceFile
          );
          return mapNewInvoiceToRequest(draft, invoicePath);
        })
      );
      const deletedInvoiceRequests = deletedInvoices.map(
        mapDeletedInvoiceToRequest
      );

      await updatePOScheduleInvoices({
        schedule_id: parsedScheduleId,
        invoices: [...newInvoiceRequests, ...deletedInvoiceRequests],
      });
      setShowSuccessModal(true);
    } catch (error) {
      setPageError(
        getInvoiceErrorMessage(
          error,
          "Unable to update invoices. Please try again."
        )
      );
    } finally {
      setIsPreparingSubmission(false);
    }
  };

  const handleSubmit = () => {
    if (!hasChanges || !validateDrafts()) return;
    if (shouldWarnAboutInvoiceQuantity()) {
      setShowQuantityWarning(true);
      return;
    }
    performSubmit();
  };

  const confirmDeleteInvoice = () => {
    if (!deleteCandidate) return;
    setDeletedInvoices((currentInvoices) => [
      ...currentInvoices,
      deleteCandidate,
    ]);
    setDeleteCandidate(null);
    setPageError("");
  };

  const isLoading =
    getPOScheduleGroupDetailsStatus === STATUS_BEGIN ||
    !getPOScheduleGroupDetailsStatus;
  const headerPurchaseOrderNumber =
    purchaseOrder?.purchase_order_number || "PO schedule invoices";

  return (
    <InvoiceEditorShell>
      <InvoiceEditorFrame>
        <InvoiceEditorHeader>
          <HeaderIconButton
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ChevronLeft aria-hidden="true" />
          </HeaderIconButton>
          <InvoiceEditorHeaderCopy>
            <h1>{headerPurchaseOrderNumber}</h1>
            <span>
              {formatWeightValue(purchaseOrder?.scheduled_sku_count) ?? "—"}{" "}
              SKUs
              {" • "}
              {formatWeight(purchaseOrder?.scheduled_weight_in_kg)}
            </span>
          </InvoiceEditorHeaderCopy>
        </InvoiceEditorHeader>

        <InvoiceEditorScrollArea>
          {!hasValidParams ? (
            <InvoiceEditorContent>
              <EmptyState>
                <StateTitle>Unable to load invoices</StateTitle>
                <ErrorMessage>The PO schedule URL is invalid.</ErrorMessage>
              </EmptyState>
            </InvoiceEditorContent>
          ) : null}

          {hasValidParams && isLoading ? (
            <ScheduleInvoiceEditorSkeleton />
          ) : null}

          {hasValidParams && getPOScheduleGroupDetailsStatus === STATUS_FAIL ? (
            <InvoiceEditorContent>
              <EmptyState>
                <StateTitle>Unable to load invoices</StateTitle>
                <ErrorMessage>{poScheduleGroupDetailsError}</ErrorMessage>
                <RetryButton type="button" onClick={fetchScheduleGroupDetails}>
                  Try again
                </RetryButton>
              </EmptyState>
            </InvoiceEditorContent>
          ) : null}

          {getPOScheduleGroupDetailsStatus === STATUS_DONE && !purchaseOrder ? (
            <InvoiceEditorContent>
              <EmptyState>
                <StateTitle>PO schedule not found</StateTitle>
                <ErrorMessage>
                  This PO schedule is not part of the selected schedule group.
                </ErrorMessage>
              </EmptyState>
            </InvoiceEditorContent>
          ) : null}

          {getPOScheduleGroupDetailsStatus === STATUS_DONE && purchaseOrder ? (
            <InvoiceEditorContent>
              <AddInvoiceButton type="button" onClick={addDraft}>
                <PlusCircle aria-hidden="true" /> Add invoice
              </AddInvoiceButton>

              <EditorInvoiceList>
                {visibleExistingInvoices.map((invoice) => {
                  const hasImageError = invoiceImageErrors[invoice.vendor_invoice_id];
                  const invoiceUrl = invoice.invoice_image_url || "";
                  const canOpen = Boolean(invoiceUrl);
                  const showImage = canOpen && !hasImageError && isImageUrl(invoiceUrl);
                  const showPdf = canOpen && !showImage && isPdfUrl(invoiceUrl);

                  return (
                    <ExistingInvoiceCard key={invoice.vendor_invoice_id}>
                      <ExistingInvoiceThumbnail
                        href={canOpen ? invoiceUrl : undefined}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Open invoice ${invoice.invoice_number}`}
                      >
                        {showImage ? (
                          <img
                            src={invoiceUrl}
                            alt=""
                            onError={() => {
                              setInvoiceImageErrors((prev) => ({
                                ...prev,
                                [invoice.vendor_invoice_id]: true,
                              }));
                            }}
                          />
                        ) : showPdf ? (
                          <EditorPdfThumbnail url={invoiceUrl} />
                        ) : (
                          <ExistingInvoiceDocumentFallback>
                            <FileText aria-hidden="true" />
                          </ExistingInvoiceDocumentFallback>
                        )}
                        {canOpen ? (
                          <Maximize2 aria-hidden="true" />
                        ) : null}
                      </ExistingInvoiceThumbnail>
                    <ExistingInvoiceCopy>
                      <strong>{invoice.invoice_number || "Invoice"}</strong>
                      <span>
                        {formatWeightValue(invoice.number_of_skus) ?? "—"} SKUs
                        {" • "}
                        {formatWeightValue(invoice.weight_in_kg) ?? "—"}kg
                        {" • "}
                        {formatInvoiceAmount(invoice.invoice_amount)}
                      </span>
                    </ExistingInvoiceCopy>
                    <DeleteIconButton
                      type="button"
                      onClick={() => setDeleteCandidate(invoice)}
                      aria-label={`Delete invoice ${invoice.invoice_number}`}
                    >
                      <Trash2 aria-hidden="true" />
                    </DeleteIconButton>
                  </ExistingInvoiceCard>
                  );
                })}

                {drafts.map((draft, draftIndex) => {
                  const error = draftErrors[draft.client_id];
                  const imageInputId = `invoice-images-${draft.client_id}`;
                  const pdfInputId = `invoice-pdf-${draft.client_id}`;

                  return (
                    <NewInvoiceCard key={draft.client_id}>
                      <NewInvoiceHeader>
                        <h2>
                          Invoice{" "}
                          {visibleExistingInvoices.length + draftIndex + 1}
                        </h2>
                        <DeleteIconButton
                          type="button"
                          onClick={() => removeDraft(draft.client_id)}
                          aria-label={`Remove invoice ${
                            visibleExistingInvoices.length + draftIndex + 1
                          }`}
                        >
                          <Trash2 aria-hidden="true" />
                        </DeleteIconButton>
                      </NewInvoiceHeader>

                      <UploadArea>
                        <HiddenFileInput
                          id={imageInputId}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          multiple
                          onChange={(event) =>
                            handleImageSelection(draft.client_id, event)
                          }
                        />
                        <HiddenFileInput
                          id={pdfInputId}
                          type="file"
                          accept="application/pdf,.pdf"
                          onChange={(event) =>
                            handlePdfSelection(draft.client_id, event)
                          }
                        />

                        {!draft.upload_type ? (
                          <UploadOptions>
                            <UploadOptionButton
                              as="label"
                              htmlFor={imageInputId}
                            >
                              <Camera aria-hidden="true" /> Upload image
                            </UploadOptionButton>
                            <UploadOptionButton as="label" htmlFor={pdfInputId}>
                              <FileText aria-hidden="true" /> Upload PDF
                            </UploadOptionButton>
                          </UploadOptions>
                        ) : null}

                        {draft.upload_type === INVOICE_UPLOAD_TYPES.images ? (
                          <ImagePreviewGrid>
                            {draft.files.map((fileItem, fileIndex) => (
                              <ImagePreview
                                key={`${fileItem.file.name}-${fileIndex}`}
                              >
                                <img src={fileItem.preview_url} alt="" />
                                <RemoveFileButton
                                  type="button"
                                  onClick={() =>
                                    removeDraftFile(draft.client_id, fileIndex)
                                  }
                                  aria-label={`Remove ${fileItem.file.name}`}
                                >
                                  <X aria-hidden="true" />
                                </RemoveFileButton>
                              </ImagePreview>
                            ))}
                            <AddImageTile as="label" htmlFor={imageInputId}>
                              <Camera aria-hidden="true" /> Add image
                            </AddImageTile>
                          </ImagePreviewGrid>
                        ) : null}

                        {draft.upload_type === INVOICE_UPLOAD_TYPES.pdf ? (
                          <PdfPreview>
                            <FileText aria-hidden="true" />
                            <span>{draft.files[0]?.file.name}</span>
                            <RemoveFileButton
                              type="button"
                              onClick={() =>
                                removeDraftFile(draft.client_id, 0)
                              }
                              aria-label={`Remove ${draft.files[0]?.file.name}`}
                            >
                              <X aria-hidden="true" />
                            </RemoveFileButton>
                          </PdfPreview>
                        ) : null}
                      </UploadArea>

                      <InvoiceFields>
                        <FieldWrapper
                          $error={error?.fields?.includes("invoice_number")}
                        >
                          {draft.invoice_number ? (
                            <FieldLabel
                              $error={error?.fields?.includes("invoice_number")}
                            >
                              Invoice number
                            </FieldLabel>
                          ) : null}
                          <FieldInput
                            $error={error?.fields?.includes("invoice_number")}
                            value={draft.invoice_number}
                            onChange={(event) =>
                              updateDraft(
                                draft.client_id,
                                "invoice_number",
                                event.target.value
                              )
                            }
                            placeholder="Invoice number"
                          />
                        </FieldWrapper>
                        <FieldWrapper
                          $error={error?.fields?.includes("invoice_amount")}
                        >
                          {draft.invoice_amount ? (
                            <FieldLabel
                              $error={error?.fields?.includes("invoice_amount")}
                            >
                              Amount
                            </FieldLabel>
                          ) : null}
                          <FieldInput
                            $error={error?.fields?.includes("invoice_amount")}
                            type="number"
                            inputMode="decimal"
                            min="0"
                            value={draft.invoice_amount}
                            onChange={(event) => {
                              const raw = event.target.value;
                              if (raw === "") {
                                updateDraft(draft.client_id, "invoice_amount", "");
                                return;
                              }
                              const parsed = Number(raw);
                              if (!Number.isFinite(parsed) || parsed < 0) return;
                              const poAmount = Number(purchaseOrder?.po_amount);
                              if (Number.isFinite(poAmount) && poAmount > 0) {
                                const maxAllowed = poAmount * (1 + invoiceAmountBufferPercent / 100);
                                // sum of existing visible invoices amounts
                                const existingAmount = visibleExistingInvoices.reduce(
                                  (sum, inv) => sum + (Number(inv.invoice_amount) || 0),
                                  0
                                );
                                // sum of other draft amounts (not this one)
                                const otherDraftsAmount = drafts.reduce(
                                  (sum, d) =>
                                    d.client_id === draft.client_id
                                      ? sum
                                      : sum + (Number(d.invoice_amount) || 0),
                                  0
                                );
                                const remaining = Math.floor((maxAllowed - existingAmount - otherDraftsAmount) * 100) / 100;
                                const clamped = parsed > remaining ? Math.max(remaining, 0) : parsed;
                                updateDraft(draft.client_id, "invoice_amount", String(clamped));
                                return;
                              }
                              updateDraft(draft.client_id, "invoice_amount", raw);
                            }}
                            placeholder="Amount"
                          />
                          <FieldSuffix>₹</FieldSuffix>
                        </FieldWrapper>
                        <FieldWrapper
                          $error={error?.fields?.includes("weight_in_kg")}
                        >
                          {draft.weight_in_kg ? (
                            <FieldLabel
                              $error={error?.fields?.includes("weight_in_kg")}
                            >
                              Net weight
                            </FieldLabel>
                          ) : null}
                          <FieldInput
                            $error={error?.fields?.includes("weight_in_kg")}
                            type="number"
                            inputMode="decimal"
                            min="0"
                            value={draft.weight_in_kg}
                            onChange={(event) => {
                              const raw = event.target.value;
                              if (raw === "") {
                                updateDraft(draft.client_id, "weight_in_kg", "");
                                return;
                              }
                              const parsed = Number(raw);
                              if (!Number.isFinite(parsed) || parsed < 0) return;
                              const scheduleWeight = Number(purchaseOrder?.scheduled_weight_in_kg);
                              if (Number.isFinite(scheduleWeight) && scheduleWeight > 0) {
                                // sum of existing visible invoices weight (excl deleted)
                                const existingWeight = visibleExistingInvoices.reduce(
                                  (sum, inv) => sum + (Number(inv.weight_in_kg) || 0),
                                  0
                                );
                                // sum of other drafts weight (not this one)
                                const otherDraftsWeight = drafts.reduce(
                                  (sum, d) =>
                                    d.client_id === draft.client_id
                                      ? sum
                                      : sum + (Number(d.weight_in_kg) || 0),
                                  0
                                );
                                const remaining = scheduleWeight - existingWeight - otherDraftsWeight;
                                const clamped = parsed > remaining ? Math.max(remaining, 0) : parsed;
                                updateDraft(draft.client_id, "weight_in_kg", String(clamped));
                                return;
                              }
                              updateDraft(draft.client_id, "weight_in_kg", raw);
                            }}
                            placeholder="Net weight"
                          />
                          <FieldSuffix>kg</FieldSuffix>
                        </FieldWrapper>
                        <FieldWrapper
                          $error={error?.fields?.includes("number_of_sku")}
                        >
                          {draft.number_of_sku ? (
                            <FieldLabel
                              $error={error?.fields?.includes("number_of_sku")}
                            >
                              No. of SKUs
                            </FieldLabel>
                          ) : null}
                          <FieldInput
                            $error={error?.fields?.includes("number_of_sku")}
                            type="number"
                            inputMode="numeric"
                            min="1"
                            step="1"
                            value={draft.number_of_sku}
                            onKeyDown={(e) => {
                              if (e.key === "." || e.key === ",") e.preventDefault();
                            }}
                            onChange={(event) => {
                              const raw = event.target.value;
                              if (raw === "") {
                                updateDraft(draft.client_id, "number_of_sku", "");
                                return;
                              }
                              const intVal = Math.floor(Math.abs(Number(raw)));
                              if (!Number.isFinite(intVal)) return;
                              const maxSkus = Number(purchaseOrder?.scheduled_sku_count);
                              const clamped =
                                Number.isFinite(maxSkus) && maxSkus > 0 && intVal > maxSkus
                                  ? maxSkus
                                  : intVal;
                              updateDraft(draft.client_id, "number_of_sku", String(clamped));
                            }}
                            placeholder="No. of SKUs"
                          />
                        </FieldWrapper>
                      </InvoiceFields>
                      {error?.message ? (
                        <CardError>{error.message}</CardError>
                      ) : null}
                    </NewInvoiceCard>
                  );
                })}
              </EditorInvoiceList>

              <InvoiceInfo>
                <Info aria-hidden="true" />
                Add invoices for the scheduled quantity of PO
              </InvoiceInfo>
              {pageError ? <PageError>{pageError}</PageError> : null}
            </InvoiceEditorContent>
          ) : null}
        </InvoiceEditorScrollArea>

        {getPOScheduleGroupDetailsStatus === STATUS_DONE && purchaseOrder ? (
          <InvoiceEditorFooter>
            <SubmitInvoicesButton
              type="button"
              disabled={!hasChanges || !areDraftsComplete || isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? "Submitting…" : "Submit"}
            </SubmitInvoicesButton>
          </InvoiceEditorFooter>
        ) : null}
      </InvoiceEditorFrame>

      {deleteCandidate && !hasShippingNote(deleteCandidate) ? (
        <ModalOverlay role="dialog" aria-modal="true">
          <ModalCard>
            <ModalIcon>
              <img src="/logos/bang.svg" alt="" aria-hidden="true" />
            </ModalIcon>
            <h2>Delete invoice {deleteCandidate.invoice_number}?</h2>
            <ModalActions>
              <ModalPrimaryButton type="button" onClick={confirmDeleteInvoice}>
                Yes, delete
              </ModalPrimaryButton>
              <ModalSecondaryButton
                type="button"
                onClick={() => setDeleteCandidate(null)}
              >
                No
              </ModalSecondaryButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}

      {deleteCandidate && hasShippingNote(deleteCandidate) ? (
        <ModalOverlay $sheet role="dialog" aria-modal="true">
          <ModalSheet>
            <ModalIcon>
              <img src="/logos/quiz_chat.svg" alt="" aria-hidden="true" />
            </ModalIcon>
            <h2>Shipping note is created for invoice</h2>
            <p>
              Deleting this invoice will update the shipping note{" "}
              {formatShippingNoteNumber(deleteCandidate)}
            </p>
            <SheetButtonRow>
              <ModalSecondaryButton
                type="button"
                $outlined
                onClick={() => setDeleteCandidate(null)}
              >
                Back
              </ModalSecondaryButton>
              <ModalPrimaryButton type="button" onClick={confirmDeleteInvoice}>
                Delete anyway
              </ModalPrimaryButton>
            </SheetButtonRow>
          </ModalSheet>
        </ModalOverlay>
      ) : null}

      <ResponsiveModal
        visible={showQuantityWarning}
        onClose={() => setShowQuantityWarning(false)}
        showCloseIcon={false}
        overlayClickClose={true}
        overlayOpacity={0.68}
        floatingProps={{ isCentered: true, style: { background: "#fff", borderRadius: 16, padding: 24, maxWidth: 480, width: "100%" } }}
      >
        <ModalIcon>
          <img src="/logos/quiz_chat.svg" alt="" aria-hidden="true" />
        </ModalIcon>
        <h2>Invoice qty &lt; Scheduled qty?</h2>
        <p>
          Please add all the invoices for the scheduled quantity before
          proceeding.
        </p>
        <SheetButtonRow>
          <ModalSecondaryButton
            type="button"
            $outlined
            onClick={() => setShowQuantityWarning(false)}
          >
            Back
          </ModalSecondaryButton>
          <ModalPrimaryButton type="button" onClick={performSubmit}>
            Proceed anyway
          </ModalPrimaryButton>
        </SheetButtonRow>
      </ResponsiveModal>

      {showSuccessModal ? (
        <ModalOverlay role="dialog" aria-modal="true">
          <ModalCard>
            <img src="/logos/tick_logo.svg" alt="" aria-hidden="true" width="72" height="72" />
            <h2>Invoices updated for {headerPurchaseOrderNumber}</h2>
            <ModalActions>
              <ModalSecondaryButton type="button" onClick={() => navigate(-1)}>
                Okay
              </ModalSecondaryButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </InvoiceEditorShell>
  );
};

export default ScheduleInvoiceEditor;
